import { Controller } from "@hotwired/stimulus"
import { openRows, setOpenRows } from "../lib/plan_row_state"

// The planner: the season's whole schedule as one editable list.
//
// Three jobs, none of which the field controllers should know about:
//   * expand/collapse a row to reveal its fields
//   * the shift-dates dialog and its live preview
//   * drag-to-reorder
//
// Reordering writes *dates*, not a position column. The round number is derived
// from date order, so "put R6 above R5" means "give it R5's slot" — the server
// redistributes the season's existing timestamps over the new order and the
// list renumbers itself on the next render.
const MAX_WEEKS = 12 // Seasons::ShiftRounds::MAX_WEEKS
const DAY_MS = 86_400_000
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

export default class extends Controller {
  static targets = [
    "row",
    "panel",
    "list",
    "handle",
    "generator",
    "shift",
    "shiftForm",
    "weeks",
    "preview",
    "shiftSubmit",
    "notify",
    "shiftDirection",
    "shiftWindow",
    "shiftError",
    "shiftNote",
  ]
  static values = {
    reorderUrl: String,
    seasonId: String,
    openRound: String,
    seasonStarts: String,
    seasonEnds: String,
  }

  // A duplicate arrives open: it is a copy you are about to change, and finding
  // it in a list where two rows now read the same is the one thing duplicating
  // makes harder. Seeded into the shared store in `initialize` so it is already
  // there when the new row's own connect() reads it.
  initialize() {
    if (!this.openRoundValue) return
    const open = openRows(this.seasonIdValue)
    if (!open.includes(this.openRoundValue)) {
      setOpenRows(this.seasonIdValue, [...open, this.openRoundValue])
    }
  }

  connect() {
    this.previewShift()
    this.restoreFocus()
  }

  // A reorder replaces the frame this controller lives in, so it is torn down
  // and rebuilt mid-interaction — focusing the handle in the callback would
  // target a detached node. The moved round's id is parked instead and picked
  // up here, which is what lets ↓ ↓ move a round two slots.
  restoreFocus() {
    const id = sessionStorage.getItem(this.focusKey)
    if (!id) return
    sessionStorage.removeItem(this.focusKey)
    const row = this.rowTargets.find((r) => r.dataset.roundId === id)
    row?.querySelector("[data-season-plan-target='handle']")?.focus()
  }

  // Rows own their own open state (see plan_row_controller): a row has to
  // restore itself, because a row-scoped Turbo replacement re-runs *its*
  // connect() and not this one.
  closeAllRows() {
    this.rowTargets.forEach((row) => {
      const panel = row.querySelector("[data-season-plan-target='panel']")
      panel?.classList.add("hidden")
      row.dataset.open = "false"
    })
    setOpenRows(this.seasonIdValue, [])
  }

  // ───── dialogs ─────

  openGenerator() {
    this.generatorTarget.showModal()
  }
  closeGenerator() {
    this.generatorTarget.close()
  }

  openShift() {
    this.previewShift()
    this.shiftTarget.showModal()
  }
  closeShift() {
    this.shiftTarget.close()
  }

  // Clicking the backdrop (i.e. the dialog element itself, not its contents)
  // closes — same affordance as the confirm dialog.
  backdropClose(event) {
    if (event.target.tagName === "DIALOG") event.target.close()
  }

  // ───── shift dates ─────

  weeksEarlier() {
    this._bumpWeeks(-1)
  }
  weeksLater() {
    this._bumpWeeks(+1)
  }

  // Zero is not a shift, so stepping lands past it rather than on it: a stepper
  // that stops at a value the primary button refuses reads as broken.
  _bumpWeeks(direction) {
    const current = parseInt(this.weeksTarget.value, 10)
    let next = (Number.isFinite(current) ? current : 0) + direction
    if (next === 0) next += direction
    this.weeksTarget.value = Math.max(-MAX_WEEKS, Math.min(MAX_WEEKS, next))
    this.previewShift()
  }

  // "Shift & notify" has to stop saying "notify" the moment the box is
  // unticked — the button is the last thing read before committing.
  syncShiftLabel() {
    if (!this.hasShiftSubmitTarget || !this.hasNotifyTarget) return
    this.shiftSubmitTarget.value = this.notifyTarget.checked
      ? "Shift & notify"
      : "Shift dates"
  }

  // Live preview of the new dates. Purely client-side arithmetic: the server
  // recomputes properly on submit, this just has to be honest about the shape
  // of the change before you commit to it.
  previewShift() {
    if (!this.hasWeeksTarget || !this.hasPreviewTarget) return

    const weeks = parseInt(this.weeksTarget.value, 10)
    const valid =
      Number.isFinite(weeks) && weeks !== 0 && Math.abs(weeks) <= MAX_WEEKS

    this._previewRows(weeks, valid)
    this._previewDirection(weeks, valid)
    this._previewWindow(weeks, valid)
    this._previewError(weeks, valid)

    if (this.hasShiftSubmitTarget) this.shiftSubmitTarget.disabled = !valid
    this.syncShiftLabel()
  }

  _previewRows(weeks, valid) {
    this.previewTarget.querySelectorAll("[data-shift-row]").forEach((row) => {
      const to = row.querySelector("[data-shift-to]")
      const from = row.querySelector("[data-shift-from]")
      if (!valid) {
        to.textContent = "—"
        from.classList.remove("line-through")
        return
      }
      from.classList.add("line-through")
      to.textContent = this._shortDate(this._shift(row.dataset.at, weeks))
    })
  }

  _previewDirection(weeks, valid) {
    if (!this.hasShiftDirectionTarget) return
    if (!valid) return (this.shiftDirectionTarget.textContent = "—")
    const n = Math.abs(weeks)
    this.shiftDirectionTarget.textContent = `${n} ${n === 1 ? "week" : "weeks"} ${weeks > 0 ? "later" : "earlier"}`
  }

  // Mirrors Seasons::ShiftRounds#shift_season_window: both ends travel with the
  // calendar, then widen to contain every round. Raced rounds don't move, so a
  // shift earlier can't drag the start past the first race that happened.
  _previewWindow(weeks, valid) {
    if (!this.hasShiftWindowTarget) return

    const dates = this.rowTargets
      .map((row) => {
        const at = new Date(row.dataset.at).getTime()
        if (!Number.isFinite(at)) return null
        return row.dataset.raced === "true" || !valid
          ? at
          : at + weeks * 7 * DAY_MS
      })
      .filter((t) => t !== null)
    if (!dates.length) return (this.shiftWindowTarget.textContent = "")

    const offset = valid ? weeks * 7 * DAY_MS : 0
    const declaredStart = this._parseDate(this.seasonStartsValue, offset)
    const declaredEnd = this._parseDate(this.seasonEndsValue, offset)

    const from = Math.min(...[declaredStart, ...dates].filter(Number.isFinite))
    const to = Math.max(...[declaredEnd, ...dates].filter(Number.isFinite))
    this.shiftWindowTarget.textContent = `Season window · ${this._longDate(new Date(from))} → ${this._longDate(new Date(to))}`
  }

  _previewError(weeks, valid) {
    if (!this.hasShiftErrorTarget) return
    this.shiftErrorTarget.classList.toggle("hidden", valid)
    if (valid) return
    this.shiftErrorTarget.textContent =
      weeks === 0 || !Number.isFinite(weeks)
        ? "Enter a non-zero number of weeks."
        : `Shifts are limited to ±${MAX_WEEKS} weeks.`
  }

  _parseDate(iso, offset) {
    if (!iso) return NaN
    const t = new Date(iso).getTime()
    return Number.isFinite(t) ? t + offset : NaN
  }

  _shift(iso, weeks) {
    const at = new Date(iso)
    at.setDate(at.getDate() + weeks * 7)
    return at
  }

  // Built by hand rather than with `toLocaleDateString`, which formats to the
  // *browser's* locale: on en-US it produced "May 31" next to the server's own
  // "24 May" in the same row, so a preview line read "24 May → May 31". These
  // match `%d %b` and `%a %d %b`, which is what every date in the planner uses.
  _shortDate(date) {
    return `${String(date.getDate()).padStart(2, "0")} ${MONTHS[date.getMonth()]}`
  }

  _longDate(date) {
    return `${WEEKDAYS[date.getDay()]} ${this._shortDate(date)}`
  }

  // ───── reorder ─────

  dragStart(event) {
    this.dragRow = event.currentTarget.closest(
      "[data-season-plan-target='row']",
    )
    event.dataTransfer.effectAllowed = "move"
    // Firefox needs data set for a drag to start at all.
    event.dataTransfer.setData("text/plain", this.dragRow.dataset.roundId)
    this.dragRow.dataset.dragging = "true"
    // Dragging closes every expanded row: an open panel is 200px of the list,
    // and "drop it two rows down" stops being a judgement anyone can make when
    // one of those rows is six times the height of the others.
    this.closeAllRows()
  }

  // The insertion marker only appears once the pointer has committed to the
  // swap — past the target's midpoint in the direction of travel. Before that
  // the drop would be a no-op, so a line there would promise a move that never
  // happens. Not preventing the default is what makes the browser refuse the
  // drop too, so the marker and the drop can never disagree.
  dragOver(event) {
    if (!this.dragRow) return
    const over = event.currentTarget

    // Raced rounds are fixed points — ReorderRounds pins them, so they reject
    // drops as well as offering no handle.
    if (over.dataset.raced === "true") return this._clearMarker()
    if (over === this.dragRow) return this._clearMarker()

    const rows = this.rowTargets
    const from = rows.indexOf(this.dragRow)
    const to = rows.indexOf(over)
    const box = over.getBoundingClientRect()
    const past = event.clientY > box.top + box.height / 2
    const committed = to > from ? past : !past

    if (!committed) return this._clearMarker()

    event.preventDefault()
    this._clearMarker()
    this.dropRow = over
    over.dataset.drop = to > from ? "below" : "above"
  }

  drop(event) {
    event.preventDefault()
    const row = this.dragRow
    const target = this.dropRow
    this._clearMarker()
    if (!row || !target) return

    const rows = this.rowTargets
    const after = rows.indexOf(target) > rows.indexOf(row)
    target.parentNode.insertBefore(row, after ? target.nextSibling : target)
    this.submitOrder()
  }

  dragEnd() {
    if (this.dragRow) delete this.dragRow.dataset.dragging
    this.dragRow = null
    this._clearMarker()
  }

  _clearMarker() {
    if (this.dropRow) delete this.dropRow.dataset.drop
    this.dropRow = null
  }

  // Keyboard equivalent of the drag. Without it reordering is mouse-only —
  // the handle is the sole control for it, and a drag has no keyboard
  // fallback of its own.
  moveRow(event) {
    let dir = 0
    if (event.key === "ArrowUp") dir = -1
    else if (event.key === "ArrowDown") dir = +1
    if (!dir) return
    event.preventDefault()

    const handle = event.currentTarget
    const row = handle.closest("[data-season-plan-target='row']")
    const sibling =
      dir < 0 ? row.previousElementSibling : row.nextElementSibling
    if (!sibling?.matches("[data-season-plan-target='row']")) return
    // Same rule as the drag: a raced round keeps its slot.
    if (sibling.dataset.raced === "true") return

    dir < 0
      ? row.parentNode.insertBefore(row, sibling)
      : row.parentNode.insertBefore(sibling, row)
    // The save replaces the whole frame, so focusing the handle we have now
    // would be undone a moment later — hand the id over and let connect()
    // re-focus the new one. Without this, holding ↓ moves a round exactly once.
    try {
      sessionStorage.setItem(this.focusKey, row.dataset.roundId)
    } catch (_) {
      /* focus is just not restored */
    }
    this.submitOrder()
  }

  async submitOrder() {
    const ids = this.rowTargets.map((row) => row.dataset.roundId)
    const body = new FormData()
    ids.forEach((id) => body.append("round_ids[]", id))

    await fetch(this.reorderUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token":
          document.querySelector("meta[name='csrf-token']")?.content ?? "",
        Accept: "text/vnd.turbo-stream.html",
      },
      body,
    })
      .then((r) => r.text())
      .then(
        (html) =>
          html.includes("<turbo-stream") &&
          window.Turbo.renderStreamMessage(html),
      )
      .catch(() => window.location.reload())
  }
}
